import { type RefObject, useEffect } from 'react';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { isBatchProcessing } from 'app/modules/wallforming/components/state-management/placement/slices/connectionSlice';
import { yieldToMain } from 'app/modules/wallforming/components/state-management/placement/utils/schedulerUtils';
import { MATRIX_UPDATE_CHUNK_SIZE } from 'shared/constants';

import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

interface InstanceMatrixSyncHandlerProps {
    articleRef: RefObject<ArticleReference>;
    instanceSelectionIdsRef: RefObject<string[]>;
    needsBatchMatrixUpdateRef: RefObject<boolean>;
    chunkUpdateInProgressRef: RefObject<boolean>;
    chunkUpdateIdRef: RefObject<number>;
    pendingUpdateIdsRef: RefObject<Set<string> | null>;
}

/**
 * Subscribes to article instance data and updates instance matrices and refs, handling chunked updates for performance.
 */
export const useInstanceMatrixSyncHandler = ({
    articleRef,
    instanceSelectionIdsRef,
    needsBatchMatrixUpdateRef,
    chunkUpdateInProgressRef,
    chunkUpdateIdRef,
    pendingUpdateIdsRef
}: InstanceMatrixSyncHandlerProps) => {
    const updateInstanceMatrices = usePlacementStore(useShallow((state) => state.updateInstanceMatrices));

    useEffect(() => {
        const subscribedArticleInstances = usePlacementStore.subscribe(
            (state) => state.articles[articleRef.current.id]?.articleInstances,
            (newInstances, prevInstances) => {
                if (!newInstances || newInstances === prevInstances) {
                    return;
                }

                const newInstanceIds = Object.keys(newInstances);

                if (isBatchProcessing && isBatchMatrixSkipEnabled()) {
                    needsBatchMatrixUpdateRef.current = true;

                    // sync instances of the article ref during batch processing
                    for (const id of newInstanceIds) {
                        if (newInstances[id] !== prevInstances?.[id]) {
                            articleRef.current.articleInstances[id] = newInstances[id];
                            if (pendingUpdateIdsRef.current === null) {
                                pendingUpdateIdsRef.current = new Set<string>();
                            }
                            pendingUpdateIdsRef.current.add(id);
                        }
                    }

                    // Skip matrix updates if no instance has changed
                    needsBatchMatrixUpdateRef.current =
                        pendingUpdateIdsRef.current !== null && pendingUpdateIdsRef.current.size > 0;

                    return;
                }

                const state = usePlacementStore.getState();
                const isDragging =
                    (state.isDragActiveRef.current && !state.isAutoDragActiveRef.current) ||
                    (state.isDragNewArticleActiveRef.current && !state.isAutoDragActiveRef.current);

                const updatedInstanceIds: string[] = [];
                for (const id of newInstanceIds) {
                    if (newInstances[id] !== prevInstances?.[id]) {
                        if (isDragging && instanceSelectionIdsRef.current.includes(id)) {
                            continue;
                        }

                        if (isDragging && !(id in (prevInstances ?? {}))) {
                            articleRef.current.articleInstances[id] = newInstances[id];
                            continue;
                        }

                        articleRef.current.articleInstances[id] = newInstances[id];
                        updatedInstanceIds.push(id);
                    }
                }

                if (updatedInstanceIds.length > 0) {
                    const showSelection = updatedInstanceIds.some((id) => instanceSelectionIdsRef.current.includes(id));

                    if (updatedInstanceIds.length <= MATRIX_UPDATE_CHUNK_SIZE) {
                        updateInstanceMatrices(showSelection, articleRef, !isDragging, updatedInstanceIds);
                    } else {
                        // chunked update to avoid blocking the main thread
                        const updateId = ++chunkUpdateIdRef.current;
                        chunkUpdateInProgressRef.current = true;
                        const updateColliders = !isDragging;

                        (async () => {
                            for (let i = 0; i < updatedInstanceIds.length; i += MATRIX_UPDATE_CHUNK_SIZE) {
                                if (chunkUpdateIdRef.current !== updateId) {
                                    break;
                                }

                                const chunk = updatedInstanceIds.slice(i, i + MATRIX_UPDATE_CHUNK_SIZE);
                                const isLastChunk = i + MATRIX_UPDATE_CHUNK_SIZE >= updatedInstanceIds.length;
                                updateInstanceMatrices(showSelection, articleRef, updateColliders, chunk);

                                if (!isLastChunk) {
                                    await yieldToMain();
                                }
                            }

                            chunkUpdateInProgressRef.current = false;
                        })();
                    }
                }
            }
        );

        return () => subscribedArticleInstances();
    }, [
        articleRef,
        updateInstanceMatrices,
        instanceSelectionIdsRef,
        needsBatchMatrixUpdateRef,
        pendingUpdateIdsRef,
        chunkUpdateIdRef,
        chunkUpdateInProgressRef
    ]);
};

export function isBatchMatrixSkipEnabled(): boolean {
    if (typeof window === 'undefined') {
        return true;
    }

    const override = (window as { __WF_SKIP_BATCH_MATRIX__?: boolean }).__WF_SKIP_BATCH_MATRIX__;
    if (typeof override === 'boolean') {
        return override;
    }

    return true;
}
